/**
 * repo-from-labels — resolve the target GitHub repository (`owner/name`) for an
 * orca project from its Linear project labels.
 *
 * A Linear project may carry a repository label of the form
 * `repo:owner/name` (e.g. `repo:flirtmachine/app`). That value is stored on
 * `orca_projects.repo` at registration and inherited by every dispatched
 * `orca_task`, so two projects can build two different repos concurrently. A
 * project with no `repo:` label leaves the column NULL, which downstream
 * resolves to the global `ORCA_DEFAULT_REPO` env value.
 *
 * Matching mirrors {@link resolveModelFromLabels}: case-insensitive on the
 * prefix and whitespace-trimmed, since Linear stores labels in the author's
 * casing. The `owner/name` value itself keeps its original casing.
 *
 * @since 1.0.0
 */
import { Config, Effect } from "effect";

/**
 * Prefix that marks a Linear project label as a repository selector. Compared
 * case-insensitively.
 *
 * @since 1.0.0
 */
export const REPO_LABEL_PREFIX = "repo:";

/**
 * Validation pattern for the `owner/name` value carried by a `repo:` label —
 * two slash-separated segments of word chars, dots, and hyphens. Matches the
 * shape GitHub accepts for `owner/repo`; anything else is rejected as
 * malformed and treated as "no repo label".
 *
 * @since 1.0.0
 */
export const REPO_NAME_PATTERN = /^[\w.-]+\/[\w.-]+$/;

/**
 * Return the raw value after the `repo:` prefix for the FIRST repo-prefixed
 * label, or `null` when no label carries the prefix. Does not validate the
 * shape — {@link parseRepoLabel} does that. The returned value keeps its
 * original casing but is whitespace-trimmed.
 *
 * @since 1.0.0
 */
export const findRepoLabelValue = (labels: ReadonlyArray<string>): string | null => {
  for (const label of labels) {
    const trimmed = label.trim();
    if (trimmed.toLowerCase().startsWith(REPO_LABEL_PREFIX)) {
      return trimmed.slice(REPO_LABEL_PREFIX.length).trim();
    }
  }
  return null;
};

/**
 * Parse a set of Linear project labels into a validated `owner/name` repo
 * string, or `null` when no `repo:` label is present OR the one present is
 * malformed. Pure — {@link resolveRepoFromLabels} adds the malformed-label
 * warning on top of this.
 *
 * @since 1.0.0
 */
export const parseRepoLabel = (labels: ReadonlyArray<string>): string | null => {
  const value = findRepoLabelValue(labels);
  if (value === null) return null;
  return REPO_NAME_PATTERN.test(value) ? value : null;
};

/**
 * Resolve the repo `owner/name` from Linear project labels, logging a warning
 * when a `repo:` label is present but its value does not match
 * {@link REPO_NAME_PATTERN} (invalid shape → `null` + warn). Returns `null`
 * with no warning when no `repo:` label is present at all.
 *
 * @since 1.0.0
 */
export const resolveRepoFromLabels = (
  labels: ReadonlyArray<string>,
): Effect.Effect<string | null, never> => {
  const value = findRepoLabelValue(labels);
  if (value === null) return Effect.succeed(null);
  if (REPO_NAME_PATTERN.test(value)) return Effect.succeed(value);
  return Effect.logWarning(
    `Ignoring malformed repo label "${REPO_LABEL_PREFIX}${value}" (expected ${REPO_LABEL_PREFIX}owner/name) — falling back to ORCA_DEFAULT_REPO`,
  ).pipe(Effect.as(null));
};

/**
 * INF-788: derive the Linear team key from an issue identifier. Linear
 * identifiers have the shape `<TEAM>-<number>` (e.g. `INF-788`, `PRP-12`), and
 * the segment before the first hyphen IS the team key. Uppercased so lookups
 * against the {@link parseTeamRepoMap} team→repo map are case-insensitive.
 *
 * @since 1.0.0
 */
export const teamKeyFromIdentifier = (identifier: string): string => identifier.split("-")[0].trim().toUpperCase();

/**
 * INF-788: validate a single parsed `ORCA_TEAM_REPO_MAP` object into a
 * team-key → `owner/name` map. The keys are uppercased so lookups by team key
 * are case-insensitive; each value must match {@link REPO_NAME_PATTERN} or it
 * is dropped with a logged warning (a typo'd repo must never silently become a
 * task's target). A non-object payload (a JSON array, string, number, or
 * `null`) yields an empty map plus a warning. Never fails.
 *
 * @since 1.0.0
 */
const buildTeamRepoMap = (parsed: unknown): Effect.Effect<ReadonlyMap<string, string>, never> => {
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return Effect.logWarning(
      `Ignoring ORCA_TEAM_REPO_MAP: expected a JSON object of team→"owner/name" — using empty team→repo map`,
    ).pipe(Effect.as(new Map<string, string>()));
  }
  const entries = Object.entries(parsed as Record<string, unknown>);
  return Effect.reduce(
    entries,
    new Map<string, string>(),
    (acc: Map<string, string>, [key, value]: readonly [string, unknown]) =>
      typeof value === "string" && REPO_NAME_PATTERN.test(value)
        ? Effect.succeed(acc.set(key.trim().toUpperCase(), value))
        : Effect.logWarning(
          `Dropping ORCA_TEAM_REPO_MAP entry "${key}": value ${
            JSON.stringify(value)
          } is not a valid ${REPO_LABEL_PREFIX}owner/name repo`,
        ).pipe(Effect.as(acc)),
  ).pipe(Effect.map((m): ReadonlyMap<string, string> => m));
};

/**
 * INF-788: parse the raw `ORCA_TEAM_REPO_MAP` env value (a JSON object of team
 * key → `owner/name`, e.g. `{"PRP":"dcgcommunications/prpose-ai"}`) into a
 * validated team→repo map used as a dispatch-time fallback for single-repo
 * teams. Malformed JSON resolves to an empty map plus a warning; individual
 * values failing {@link REPO_NAME_PATTERN} are dropped with a warning
 * ({@link buildTeamRepoMap}). Never fails — a bad env value degrades to "no
 * team defaults", never crashes config load.
 *
 * @since 1.0.0
 */
export const parseTeamRepoMap = (raw: string): Effect.Effect<ReadonlyMap<string, string>, never> =>
  Effect.try({
    try: (): unknown => JSON.parse(raw),
    catch: () => "malformed" as const,
  }).pipe(
    Effect.matchEffect({
      onFailure: () =>
        Effect.logWarning(
          `Ignoring malformed ORCA_TEAM_REPO_MAP (not valid JSON) — using empty team→repo map`,
        ).pipe(Effect.as(new Map<string, string>() as ReadonlyMap<string, string>)),
      onSuccess: (parsed) => buildTeamRepoMap(parsed),
    }),
  );

/**
 * INF-788: raw `ORCA_TEAM_REPO_MAP` config, defaulting to `{}` when unset. The
 * `Config.withDefault(...)` sits on the same physical line as the
 * `.string("ORCA_TEAM_REPO_MAP")` selector so the Env Var Check gate reads it as
 * a defaulted (exempt) config — it must NOT be listed in `.env.production`.
 *
 * @since 1.0.0
 */
const orcaTeamRepoMapRawConfig = Config.string("ORCA_TEAM_REPO_MAP").pipe(Config.withDefault("{}"));

/**
 * INF-788: config-resolved team→repo map. Parses {@link orcaTeamRepoMapRawConfig}
 * via {@link parseTeamRepoMap} (which never fails — a bad value degrades to an
 * empty map). `Effect.orDie` strips the residual `ConfigError` type so callers
 * on a `never`-error path (the project-registration webhook) can consume it
 * directly; a genuine config-provider failure is a startup defect, not
 * something to swallow. Threaded into the dispatcher alongside
 * `ORCA_DEFAULT_REPO`.
 *
 * @since 1.0.0
 */
export const orcaTeamRepoMapConfig: Effect.Effect<ReadonlyMap<string, string>, never> = orcaTeamRepoMapRawConfig.pipe(
  Effect.flatMap(parseTeamRepoMap),
  Effect.orDie,
);
